import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { NavLink, Link } from 'react-router-dom';
import arrowDown from '../../assets/arrow-down.svg';
import uk from '../../assets/GB.png';
import de from '../../assets/DE.png';
import { ReactComponent as MenuIcon } from '../../assets/menu.svg';
import starGidLogo from '../../assets/stargid.svg';
import styles from '../styles/Header.module.scss';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootStore } from '../../store/store';

const STATIC_LINKS = [
  { text: 'About Us', link: 'about-us', hideOnAuth: false },
  { text: 'How it works', link: 'how-it-works', hideOnAuth: false },
  { text: 'Login', link: 'login', hideOnAuth: true },
];

type IHeaderProps = {
  isLandingPage?: boolean;
};

const MenuItems = () => {
  const token = useSelector((state: RootStore) => state.ui.token);

  return (
    <>
      {STATIC_LINKS.map(({ link, text, hideOnAuth }, i) => {
        if (token && hideOnAuth) return null;

        return (
          <li key={i}>
            <NavLink to={link} activeClassName={styles.active}>
              {text}
            </NavLink>
          </li>
        );
      })}
    </>
  );
};

const SideBar = () => {
  return (
    <div className={styles.sideBar}>
      <div className={styles.backdrop} onClick={() => toggleSideBar()}></div>
      <div className={styles.content}>
        <Logo />
        <ul>
          <li>
            <Link to="signup" className={styles.signup}>
              Sign up
            </Link>
          </li>
          <MenuItems />
        </ul>
      </div>
    </div>
  );
};

const toggleSideBar = () => {
  const sideBar = document.querySelector(`.${styles.sideBar}`);
  if (sideBar) {
    sideBar.classList.toggle(styles.open);
  }
};

const Logo = () => {
  return (
    <div className={styles.logo}>
      <Link to="./">
        <img src={starGidLogo} alt="Stargid" /> Stargid
      </Link>
    </div>
  );
};

const Header = ({ isLandingPage }: IHeaderProps) => {
  const token = useSelector((state: RootStore) => state.ui.token);

  const [, i18n] = useTranslation('common');
  const [fixedTop, setFixedTop] = useState(false);
  const [isOnLandingPage, setIsOnLandingPage] = useState(isLandingPage);

  const [lang, setLang] = useState(localStorage.getItem('lang') || 'en');
  const [showLangSelect, setShowLangSelect] = useState(false);

  const langSelectRef = useRef<any>();
  useClickOutside(langSelectRef, () => setShowLangSelect(false));

  useEffect(() => {
    localStorage.setItem('lang', lang);
  }, [lang]);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > (isOnLandingPage ? 1000 : 300)) {
        setFixedTop(true);
        setIsOnLandingPage(false);
      } else {
        setFixedTop(false);
        setIsOnLandingPage(isLandingPage);
      }
    };
    window.onscroll = () => onScroll();
    onScroll();
    return () => {
      window.onscroll = null;
    };
  }, [isLandingPage, isOnLandingPage]);

  const HeaderData = (
    <>
      <header className={[styles.header, isOnLandingPage ? styles.landingPage : ''].join(' ')}>
        <Logo />
        <nav className={styles.nav}>
          <ul>
            <MenuItems />
            {token ? (
              <li>
                <Link to="dashboard" className={styles.signup}>
                  Dashboard
                </Link>
              </li>
            ) : (
              <>
                <li>
                  <Link to="signup" className={styles.signup}>
                    Sign up
                  </Link>
                </li>
              </>
            )}
            <li className={styles.langSelector}>
              <a
                onClick={(e) => {
                  e.preventDefault();
                  setShowLangSelect(true);
                }}
                href="#lang"
                className={styles.langSelect}
              >
                <img className={styles.flag} src={lang === 'de' ? de : uk} alt="Language" />
                {lang}
                <img className={styles.arrow} src={arrowDown} alt="" />
              </a>
              <div ref={langSelectRef} style={{ display: showLangSelect ? 'flex' : 'none' }} className={styles.languages}>
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    setLang('en');
                    i18n.changeLanguage('en');
                    setShowLangSelect(false);
                  }}
                  href="/"
                >
                  English
                </a>
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    setLang('de');
                    i18n.changeLanguage('de');
                    setShowLangSelect(false);
                  }}
                  href="/"
                >
                  Deutsch
                </a>
              </div>
            </li>
          </ul>
          <button className={styles.menu} onClick={() => toggleSideBar()}>
            <MenuIcon />
          </button>
        </nav>
      </header>
      <SideBar />
    </>
  );

  if (isOnLandingPage && !fixedTop) return HeaderData;

  return (
    <>
      <div className={[styles.headerContainer, fixedTop ? styles.fixedTop : ' '].join(' ')}>{HeaderData}</div>
      <div className={fixedTop ? styles.headerPlaceholder : ' '}></div>
    </>
  );
};

export default Header;
